import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { StarRating } from '../components/StarRating';
import * as Icons from 'lucide-react';
import { ArrowRight, CheckCircle } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { getProducts, getReviews, getPublicApiOrigin } from '../lib/api';
import { useClinicBranding } from '../context/ClinicBrandingContext';

const HERO_IMAGE =
  'https://images.unsplash.com/photo-1758654859934-2a03792260a0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwxfHxwb2RpYXRyaXN0JTIwZm9vdCUyMGNsaW5pYyUyMHBhdGllbnR8ZW58MHx8fHwxNzc1NDgwNzE2fDA&ixlib=rb-4.1.0&q=85&w=1200';
const CLINIC_IMAGE =
  'https://images.unsplash.com/photo-1648775507324-b48dd3791fa5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBjbGluaWMlMjByZWNlcHRpb24lMjBsaWdodCUyMHdvb2QlMjBwbGFudHN8ZW58MHx8fHwxNzc1NDgwNzE2fDA&ixlib=rb-4.1.0&q=85&w=800';

const DEFAULT_FEATURES = [
  { icon: 'shield', title: 'Profesionalismo', desc: 'Personal certificado y capacitado' },
  { icon: 'heart', title: 'Cuidado Integral', desc: 'Atención personalizada para cada paciente' },
  { icon: 'clock', title: 'Puntualidad', desc: 'Respetamos tu tiempo' },
  { icon: 'calendar', title: 'Flexibilidad', desc: 'Horarios que se adaptan a ti' },
];

const DEFAULT_SERVICES = [
  'Tratamiento de uñas encarnadas',
  'Cuidado de callos y durezas',
  'Tratamiento de hongos',
  'Podología deportiva',
  'Cuidado del pie diabético',
  'Plantillas personalizadas',
];

function iconFromApiName(name) {
  const pascal = String(name || 'shield')
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join('');
  return Icons[pascal] || Icons.Shield;
}

export default function HomePage() {
  const { info } = useClinicBranding();
  const [products, setProducts] = useState([]);
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [productsData, reviewsData] = await Promise.all([getProducts(), getReviews()]);
        setProducts(productsData.slice(0, 3));
        setReviews(reviewsData.slice(0, 3));
      } catch (error) {
        console.error('Error loading data:', error);
      }
    };
    loadData();
  }, []);

  const brand = info?.name || 'PodoclinicAM';

  const heroBadge = info?.hero_badge_text ?? 'Especialistas en Podología';
  const heroPrefix = info?.hero_title_prefix ?? 'Cuidamos la salud de';
  const heroHighlight = info?.hero_title_highlight ?? 'tus pies';
  const heroSubtitle =
    info?.hero_subtitle ??
    `En ${brand} brindamos atención profesional y personalizada para el cuidado integral de tus pies. Tu bienestar es nuestra prioridad.`;
  const heroImg = info?.hero_image_url?.trim() || HERO_IMAGE;

  const features = useMemo(() => {
    const raw = info?.home_features;
    if (Array.isArray(raw) && raw.length > 0) return raw.map((x) => ({ icon: x.icon, title: x.title, desc: x.desc }));
    return DEFAULT_FEATURES;
  }, [info]);

  const services = useMemo(() => {
    const bullets = info?.services_bullets;
    return Array.isArray(bullets) && bullets.some((s) => String(s).trim()) ? bullets : DEFAULT_SERVICES;
  }, [info]);

  const servicesTitle = info?.services_section_title ?? 'Nuestros Servicios';
  const servicesIntro =
    info?.services_section_intro ??
    'Ofrecemos una amplia gama de tratamientos podológicos para cuidar la salud de tus pies.';
  const clinicImg = info?.clinic_section_image_url?.trim() || CLINIC_IMAGE;

  const prodTitle = info?.featured_products_title ?? 'Servicios Destacados';
  const prodIntro =
    info?.featured_products_intro ?? 'Conoce algunos de nuestros tratamientos más solicitados';

  const reviewsTitle = info?.reviews_section_title ?? 'Lo que dicen nuestros pacientes';

  const ctaTitle = info?.cta_title ?? '¿Listo para cuidar tus pies?';
  const ctaSub =
    info?.cta_subtitle ?? 'Agenda tu cita hoy y da el primer paso hacia el bienestar de tus pies.';

  const contactTitle = info?.contact_section_title ?? 'Encuéntranos';
  const footerHoursWeekdays = info?.footer_hours_weekdays ?? 'Lunes a Viernes: 9:00 AM - 5:00 PM';
  const footerHoursLunch = info?.footer_hours_lunch ?? 'Hora de comida: 2:00 PM - 3:00 PM';
  const footerHoursWeekend = info?.footer_hours_weekend ?? 'Sábado y Domingo: Cerrado';

  const addr = info?.address ?? 'Imán, Miguel Hidalgo, 43998 Cd Sahagún, Hgo.';
  const phone = info?.phone ?? '+52 1 595 108 9300';
  const email = info?.email ?? 'danielortegalozano@gmail.com';

  return (
    <div className="min-h-screen" data-testid="home-page">
      <section className="relative overflow-hidden bg-gradient-to-br from-sage-50 to-background" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-4 py-16 md:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6 animate-fadeIn">
              <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium">
                <CheckCircle className="w-4 h-4" />
                {heroBadge}
              </div>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground tracking-tight leading-tight">
                {heroPrefix}{' '}
                <span className="text-primary">{heroHighlight}</span>
              </h1>
              <p className="text-lg text-muted-foreground leading-relaxed max-w-lg">{heroSubtitle}</p>
              <div className="flex flex-wrap gap-4 pt-2">
                <Link to="/agendar">
                  <Button size="lg" className="rounded-full text-base px-8 gap-2" data-testid="hero-book-btn">
                    Agendar Cita
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
                <Link to="/productos">
                  <Button variant="outline" size="lg" className="rounded-full text-base px-8" data-testid="hero-services-btn">
                    Ver Servicios
                  </Button>
                </Link>
              </div>
            </div>
            <div className="relative">
              <div className="absolute inset-0 bg-primary/20 rounded-3xl transform rotate-3" />
              <img
                src={heroImg}
                alt={brand}
                className="relative rounded-3xl shadow-ambient-lg w-full h-[400px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {features.map((feature, index) => {
              const Cmp = iconFromApiName(feature.icon);
              return (
                <Card key={index} className="border border-border p-6 text-center card-hover">
                  <CardContent className="p-0 space-y-3">
                    <div className="w-12 h-12 mx-auto bg-primary/10 rounded-xl flex items-center justify-center">
                      <Cmp className="w-6 h-6 text-primary" />
                    </div>
                    <h3 className="font-heading font-semibold text-foreground">{feature.title}</h3>
                    <p className="text-sm text-muted-foreground">{feature.desc}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-16 bg-sage-50/50" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-6">{servicesTitle}</h2>
              <p className="text-muted-foreground mb-8">{servicesIntro}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {services.map((service, index) => (
                  <div key={index} className="flex items-center gap-3 bg-white p-4 rounded-xl border border-border">
                    <CheckCircle className="w-5 h-5 text-success flex-shrink-0" />
                    <span className="text-foreground">{service}</span>
                  </div>
                ))}
              </div>
              <div className="mt-8">
                <Link to="/productos">
                  <Button variant="outline" className="rounded-full gap-2">
                    Ver todos los servicios
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
              </div>
            </div>
            <div>
              <img src={clinicImg} alt={brand} className="rounded-3xl shadow-ambient w-full h-[400px] object-cover" />
            </div>
          </div>
        </div>
      </section>

      {products.length > 0 && (
        <section className="py-16 bg-white" data-testid="products-preview">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-4">{prodTitle}</h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">{prodIntro}</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {products.map((product) => (
                <Card key={product.id} className="border border-border overflow-hidden card-hover" data-testid={`product-card-${product.id}`}>
                  {product.image_url && (
                    <img
                      src={`${getPublicApiOrigin()}${product.image_url}`}
                      alt={product.name}
                      className="w-full h-48 object-cover"
                    />
                  )}
                  {!product.image_url && (
                    <div className="w-full h-48 bg-sage-100 flex items-center justify-center">
                      <span className="text-sage-400 text-sm">Sin imagen</span>
                    </div>
                  )}
                  <CardContent className="p-6">
                    <h3 className="font-heading font-semibold text-lg mb-2">{product.name}</h3>
                    <p className="text-muted-foreground text-sm mb-4 line-clamp-2">{product.description}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-xl font-bold text-primary">${product.price.toFixed(2)}</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
            <div className="text-center mt-8">
              <Link to="/productos">
                <Button className="rounded-full gap-2">
                  Ver todos los servicios
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {reviews.length > 0 && (
        <section className="py-16 bg-sage-50/50" data-testid="reviews-preview">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-4">{reviewsTitle}</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {reviews.map((review) => (
                <Card key={review.id} className="border border-border p-6" data-testid={`review-card-${review.id}`}>
                  <CardContent className="p-0 space-y-4">
                    <StarRating rating={review.rating} />
                    <p className="text-foreground italic">&quot;{review.comment}&quot;</p>
                    <p className="text-sm text-muted-foreground font-medium">— {review.patient_name}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
            <div className="text-center mt-8">
              <Link to="/resenas">
                <Button variant="outline" className="rounded-full gap-2">
                  Ver todas las reseñas
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}

      <section className="py-16 bg-primary" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary-foreground mb-4">{ctaTitle}</h2>
          <p className="text-primary-foreground/80 text-lg mb-8">{ctaSub}</p>
          <Link to="/agendar">
            <Button
              size="lg"
              variant="secondary"
              className="rounded-full text-base px-8 gap-2 bg-white text-primary hover:bg-white/90"
              data-testid="cta-book-btn"
            >
              Agendar Cita Ahora
              <ArrowRight className="w-4 h-4" />
            </Button>
          </Link>
        </div>
      </section>

      <section className="py-16 bg-white" data-testid="contact-section">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-4">{contactTitle}</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <Card className="border border-border p-8">
              <CardContent className="p-0 space-y-4">
                <h3 className="font-heading text-xl font-semibold">Información de Contacto</h3>
                <div className="space-y-3 text-muted-foreground">
                  <p>
                    <strong className="text-foreground">Dirección:</strong> {addr}
                  </p>
                  <p>
                    <strong className="text-foreground">Teléfono:</strong> {phone}
                  </p>
                  <p>
                    <strong className="text-foreground">Email:</strong> {email}
                  </p>
                </div>
              </CardContent>
            </Card>
            <Card className="border border-border p-8">
              <CardContent className="p-0 space-y-4">
                <h3 className="font-heading text-xl font-semibold">Horarios de Atención</h3>
                <div className="space-y-3 text-muted-foreground">
                  <p>{footerHoursWeekdays}</p>
                  <p>{footerHoursLunch}</p>
                  <p>{footerHoursWeekend}</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
}
